import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAllResources,
  deleteResource,
  updateResource,
  setResource,
  archiveResource,
  restoreResource,
  bulkArchiveResource,
  bulkRestoreResource,
  bulkDeleteResource,
} from '../../redux/actions/resource';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Alert from '@material-ui/lab/Alert';

import Loader from '../../../../components/Loader';
import EnhancedTable from '../../components/Table';
import PageHeader from '../../components/PageHeader';
import DeleteDialog from 'components/DeleteDialog';
import InputField from 'containers/SuperAdmin/components/InputField';
import FilterBar from '../../components/FilterBar';

import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import ArchiveIcon from '@material-ui/icons/Archive';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import SettingDialog from 'containers/SuperAdmin/components/SettingDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  alert: {
    margin: theme.spacing(1),
  },
}));

export default function Resource({ titleName, id }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { resources, fetching, actionLoading } = useSelector(
    ({ admin }) => admin.resource
  );

  const [view, setView] = useState('normal');
  const [deleteId, setDeleteId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [order, setOrder] = useState(null);
  const [nameId, setNameId] = useState(null);
  const [name, setName] = useState(null);

  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [searchString, setSearchString] = useState('');
  const [sort, setSort] = useState({ orderBy: 'createdAt', orderDir: 'desc' });

  const loadPage = (page, sortOptions = {}) => {
    let query = { page, limit: resources.limit, ...sortOptions };
    if (selectedTopics) {
      query = { ...query, topics: selectedTopics.toString() };
    }
    if (selectedGroup) {
      query = { ...query, group: selectedGroup };
    }
    if (searchString !== '') {
      query = { ...query, search: searchString };
    }
    if (view === 'archived') {
      query.isArchived = true;
    }
    dispatch(fetchAllResources(query));
    setSort(sortOptions);
  };

  const headCells = [
    {
      id: 'checkbox',
      label: 'Select',
    },
    {
      id: 'title',
      label: 'Title',
      formatter: (r) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {nameId === r._id ? (
            <InputField
              initialValue={r.title}
              field={name}
              setField={setName}
              onCancel={() => {
                setName(null);
                setNameId(null);
              }}
              onSave={(d) => dispatch(updateResource(r._id, { title: d }))}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginLeft: 15 }}>{r.title}</span>
              <IconButton
                onClick={() => {
                  setName(r.title);
                  setNameId(r._id);
                }}
                size='small'
              >
                <EditIcon fontSize={'small'} />
              </IconButton>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'order',
      label: 'Order',
      formatter: (r) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {orderId === r._id ? (
            <InputField
              initialValue={r.order}
              field={order}
              setField={setOrder}
              onCancel={() => {
                setOrder(null);
                setOrderId(null);
              }}
              onSave={(d) => dispatch(updateResource(r._id, { order: d }))}
            />
          ) : (
            <>
              <span>{r.order}</span>
              <IconButton
                onClick={() => {
                  setOrder(r.order);
                  setOrderId(r._id);
                }}
                size='small'
              >
                <EditIcon fontSize={'small'} />
              </IconButton>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      actions:
        view === 'normal'
          ? [
              {
                label: 'Edit',
                action: (r) => {
                  history.push(`/superadmin/resources/edit/${r._id}`);
                },
                icon: <EditIcon />,
                disabled: () => false,
              },
              {
                label: 'Archive',
                action: (r) => {
                  setDeleteId(r._id);
                  setOpenDeleteDialog(true);
                },
                icon: <ArchiveIcon />,
                disabled: () => false,
              },
            ]
          : [
              {
                label: 'Restore',
                action: (r) => {
                  dispatch(restoreResource(r._id));
                },
                icon: <RestoreFromTrashIcon />,
                disabled: () => false,
              },
              {
                label: 'Permanently Delete',
                action: (r) => {
                  setDeleteId(r._id);
                  setOpenDeleteDialog(true);
                },
                icon: <DeleteForeverIcon />,
                disabled: () => false,
              },
            ],
    },
  ];

  let tableProps = {
    headCells: headCells,
    orderField: sort.orderBy,
    orderDirection: sort.orderDir,
    view,
    handlePage: loadPage,
    handleOrder: (d) => loadPage(resources.page, d),
    handleBulk: (ids) => {
      if (view === 'normal') {
        dispatch(bulkArchiveResource(ids));
      } else {
        dispatch(bulkRestoreResource(ids));
      }
    },
    handleBulkDelete: (ids) => {
      dispatch(bulkDeleteResource(ids));
    },
  };

  if (selectedTopics?.length > 0) {
    tableProps.handleRowOrder = (data) => {
      dispatch(setResource(data));
    };
  }

  const handleSearch = (d) => {
    setSearchString(d);
    let update = {
      page: resources.page,
      limit: resources.limit,
      search: d,
      ...sort,
    };
    if (selectedTopics) {
      update = { ...update, topics: selectedTopics.toString() };
    }
    if (selectedGroup) {
      update = { ...update, group: selectedGroup };
    }
    if (view === 'archived') {
      update.isArchived = true;
    }
    dispatch(fetchAllResources(update));
  };

  const handleAction = () => {
    let update = {
      page: resources.page,
      limit: resources.limit,
      ...sort,
    };
    if (view === 'normal') {
      update = { ...update, isArchived: true, page: 1, limit: 25 };
    }
    dispatch(fetchAllResources(update));
    setView(view === 'normal' ? 'archived' : 'normal');
  };

  useEffect(() => {
    dispatch(fetchAllResources());
  }, [dispatch]);

  const handleTopic = (e) => {
    setSelectedTopics(e.topics);
    setSelectedGroup(e.group);
    let update = {
      page: resources.page,
      limit: resources.limit,
      topics: e.topics.toString(),
      group: e.group,
      ...sort,
    };
    if (searchString !== '') {
      update = { ...update, search: searchString };
    }
    dispatch(fetchAllResources(update));
  };

  return (
    <div className={classes.root}>
      <Loader fetching={fetching || actionLoading} />
      <PageHeader
        disableSearch
        title={titleName}
        options={
          <>
            <SettingDialog
              id={id}
              name={titleName}
              enableContentEditor={true}
            />
            <IconButton
              onClick={() => {
                history.push('/superadmin/resources/new');
              }}
            >
              <AddIcon />
            </IconButton>
          </>
        }
      />
      <FilterBar
        onSearch={handleSearch}
        onAction={handleAction}
        toggle={view === 'archived'}
        topics={selectedTopics}
        group={selectedGroup}
        onTopicSelect={handleTopic}
      />
      <Grid container justifyContent='center' className={classes.alert}>
        <Alert variant='filled' color='info' icon={<InfoOutlined />}>
          The drag and drop will activate once you have selected a child
          topic/group
        </Alert>
      </Grid>
      {resources && resources.docs.length > 0 && (
        <Grid container direction='row' justifyContent='flex-start' spacing={2}>
          <Grid item sm={12} xs={12}>
            <EnhancedTable
              data={{
                ...resources,
                docs: resources.docs.filter((x) =>
                  view === 'normal' ? !x.isArchived : x.isArchived
                ),
              }}
              {...tableProps}
            />
          </Grid>
        </Grid>
      )}
      <DeleteDialog
        open={openDeleteDialog}
        handleClose={() => setOpenDeleteDialog(false)}
        handleConfirm={() => {
          if (view === 'archived') {
            dispatch(deleteResource(deleteId));
          } else {
            dispatch(archiveResource(deleteId));
          }
          setDeleteId(null);
          setOpenDeleteDialog(false);
        }}
      />
    </div>
  );
}
